import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import TextInput from '~components/FormFields/v2/TextInput';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { VetaCenterConnection } from '~hooks/useVetaCenter/models';
import useVetaCenter from '~hooks/useVetaCenter/useVetaCenter';
import useSingleFlight from '~utils/hooks/useSingleFlight';

import readVetaCenterRejection from './readVetaCenterRejection';

export const OWN_FLEET_HAULER_COMPANY_SECTION_ID = 'own-fleet-hauler-company';

const ownFleetSchema = yup.object().shape({
  guid: yup
    .string()
    .trim()
    .defined()
    .test(
      'guid-or-blank',
      t('administration.integration.veta_center.own_fleet.invalid_guid'),
      (guid) => !guid || yup.string().uuid().isValidSync(guid),
    ),
});

type OwnFleetDto = yup.InferType<typeof ownFleetSchema>;

interface VetaCenterOwnFleetFormProps {
  companyId: string;
  onSaved: (vetaCenterConnection: VetaCenterConnection) => Promise<void>;
  vetaCenterConnection: VetaCenterConnection;
}

export default function VetaCenterOwnFleetForm({
  companyId,
  onSaved,
  vetaCenterConnection,
}: VetaCenterOwnFleetFormProps) {
  const { updateOwnFleetHaulerCompanyGuid } = useVetaCenter();
  const { isInFlight: isSaving, runSingleFlight } = useSingleFlight();

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    setError,
    setValue,
  } = useForm<OwnFleetDto>({
    resolver: yupResolver(ownFleetSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { guid: vetaCenterConnection.externalOwnFleetHaulerCompanyGuid ?? '' },
  });

  const handleSave = handleSubmit(({ guid }) =>
    runSingleFlight(async () => {
      try {
        const updated = await updateOwnFleetHaulerCompanyGuid(companyId, guid || null);
        if (updated) await onSaved(updated);
      } catch (error) {
        setError('guid', {
          message:
            readVetaCenterRejection(error).message ??
            t('error_messages.veta_center.failed_to_update_connection'),
        });
      }
    }),
  );

  return (
    <Box id={OWN_FLEET_HAULER_COMPANY_SECTION_ID} sx={{ mt: 4, maxWidth: '560px' }}>
      <Typography variant="h6">
        {t('administration.integration.veta_center.own_fleet.title')}
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: 14, mb: 2 }}>
        {t('administration.integration.veta_center.own_fleet.helper')}
      </Typography>
      {vetaCenterConnection.externalOwnFleetHaulerCompanyGuid ? null : (
        <Typography sx={{ color: 'text.secondary', mb: 1 }}>
          {t('administration.integration.veta_center.own_fleet.not_set')}
        </Typography>
      )}
      <Box
        component="form"
        noValidate
        onSubmit={handleSave}
        sx={{ alignItems: 'flex-end', display: 'flex', gap: 2 }}
      >
        <TextInput
          control={control}
          dataUi="veta-center-own-fleet-hauler-company-guid"
          isDisabled={isSaving}
          label={t('administration.integration.veta_center.own_fleet.label')}
          name="guid"
        />
        <SmallButton
          color="brandV2Black"
          disabled={isSaving || !vetaCenterConnection.externalOwnFleetHaulerCompanyGuid}
          onClick={() => setValue('guid', '', { shouldDirty: true })}
          sx={{ borderColor: 'divider', color: 'text.primary', flexShrink: 0 }}
          variant="outlined"
        >
          {t('administration.integration.veta_center.own_fleet.clear')}
        </SmallButton>
        <SmallLoadingButton
          color="brandV2Yellow"
          disabled={!isDirty}
          loading={isSaving}
          type="submit"
          sx={{ flexShrink: 0 }}
        >
          {t('actions.save')}
        </SmallLoadingButton>
      </Box>
    </Box>
  );
}
